import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { departmentsAPI, employeesAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import PageHeader from '../components/ui/PageHeader';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge, { StatusBadge, RoleBadge } from '../components/ui/Badge';
import Alert from '../components/ui/Alert';
import Spinner from '../components/ui/Spinner';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import { useTranslation } from 'react-i18next';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import IntervalPicker from '../components/IntervalPicker';
import {
  canEditIPFieldForEmployeeCompany,
  canEditAnyIPForEmployeeCompany,
} from '../utils/ipPermissions';
import { useToast } from '../context/ToastContext';

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const SwitchIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
    />
  </svg>
);

const EmployeeDetails = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Parolă de acces (vizualizare/regenerare pentru angajați fără email)
  const [accessPwd, setAccessPwd] = useState(null); // parola dezvăluită (string)
  const [accessPwdState, setAccessPwdState] = useState(null); // răspuns backend: { available, reason, firstLogin, passwordEmailSent? }
  const [accessPwdLoading, setAccessPwdLoading] = useState(false);
  const [accessPwdRegenerating, setAccessPwdRegenerating] = useState(false);
  const [showResetPwdModal, setShowResetPwdModal] = useState(false);
  const [resetPwdReason, setResetPwdReason] = useState('');
  const [resetPwdDetails, setResetPwdDetails] = useState('');

  // Destinație stabilă pentru „Înapoi" — aceeași țintă ca breadcrumb-ul, NU navigate(-1).
  // navigate(-1) e fragil: dacă userul ajunge aici dintr-o notificare sau link direct,
  // butonul l-ar scoate din aplicație sau l-ar duce la o pagină random. Calculăm o țintă logică.
  const backPath = employee?.companyId
    ? `/employees?companyId=${employee.companyId}`
    : '/employees';
  const goBack = () => navigate(backPath);

  // Modal state: transfer departament
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Modal state: schimba functia
  const [showJobModal, setShowJobModal] = useState(false);
  const [newJobTitle, setNewJobTitle] = useState('');
  const [savingJob, setSavingJob] = useState(false);

  // Modal state: SUPER_ADMIN reset signature (recovery)
  const [showResetSignatureModal, setShowResetSignatureModal] = useState(false);
  const [resetSignatureReason, setResetSignatureReason] = useState('');
  const [resettingSignature, setResettingSignature] = useState(false);

  // Modal state: change last name
  const [showChangeNameModal, setShowChangeNameModal] = useState(false);
  const [newLastName, setNewLastName] = useState('');
  const [changeNameReason, setChangeNameReason] = useState('');
  const [changeNameFile, setChangeNameFile] = useState(null);
  const [changingName, setChangingName] = useState(false);

  // Modal state: change route
  const [showChangeRouteModal, setShowChangeRouteModal] = useState(false);
  const [routeForm, setRouteForm] = useState({
    homeAddress: '',
    residenceAddress: '',
    workAddress: '',
    transportMode: '',
    transportModeOther: '',
    routeDetails: '',
    routeDuration: '',
  });
  // Toggle pentru un mijloc de transport în formularul admin (transportMode e string cu virgule).
  const toggleRouteTransport = (val) => {
    setRouteForm((prev) => {
      const list = prev.transportMode
        ? prev.transportMode
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      const has = list.includes(val);
      const next = has ? list.filter((m) => m !== val) : [...list, val];
      return {
        ...prev,
        transportMode: next.join(','),
        transportModeOther: val === 'altele' && has ? '' : prev.transportModeOther,
      };
    });
  };
  const [changeRouteReason, setChangeRouteReason] = useState('');
  const [changeRouteFile, setChangeRouteFile] = useState(null);
  const [changingRoute, setChangingRoute] = useState(false);

  const openChangeRouteModal = () => {
    setRouteForm({
      homeAddress: employee?.homeAddress || '',
      residenceAddress: employee?.residenceAddress || '',
      workAddress: employee?.workAddress || '',
      // Migrăm „mixt" (scos) la personal+public dacă apare în datele vechi.
      transportMode: (employee?.transportMode || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .flatMap((m) => (m === 'mixt' ? ['personal', 'public'] : [m]))
        .join(','),
      transportModeOther: employee?.transportModeOther || '',
      routeDetails: employee?.routeDetails || '',
      routeDuration: employee?.routeDuration != null ? String(employee.routeDuration) : '',
    });
    setChangeRouteReason('');
    setChangeRouteFile(null);
    setShowChangeRouteModal(true);
  };

  const handleChangeRoute = async () => {
    setChangingRoute(true);
    try {
      const fd = new FormData();
      // Trimitem toate câmpurile; backend detectează ce s-a schimbat
      Object.entries(routeForm).forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, v);
      });
      if (changeRouteReason.trim()) fd.append('reason', changeRouteReason.trim());
      if (changeRouteFile) fd.append('supportingDoc', changeRouteFile);
      await api.post(`/employees/${id}/change-route`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(t('changeData.successRoute'), 'success');
      setShowChangeRouteModal(false);
      fetchEmployee();
    } catch (err) {
      showToast(err.response?.data?.error || t('changeData.errorRouteGeneric'), 'error');
    } finally {
      setChangingRoute(false);
    }
  };

  const handleChangeName = async () => {
    if (!newLastName.trim()) {
      showToast(t('changeData.errorMissingName'), 'error');
      return;
    }
    setChangingName(true);
    try {
      const fd = new FormData();
      fd.append('newLastName', newLastName.trim());
      if (changeNameReason.trim()) fd.append('reason', changeNameReason.trim());
      if (changeNameFile) fd.append('supportingDoc', changeNameFile);
      await api.post(`/employees/${id}/change-name`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(t('changeData.successName'), 'success');
      setShowChangeNameModal(false);
      setNewLastName('');
      setChangeNameReason('');
      setChangeNameFile(null);
      fetchEmployee();
    } catch (err) {
      showToast(err.response?.data?.error || t('changeData.errorGeneric'), 'error');
    } finally {
      setChangingName(false);
    }
  };

  // Derivă dacă userul curent poate modifica datele esențiale ale acestui angajat
  // (permisiunile reale sunt verificate pe backend — aici doar pentru a afișa/ascunde butonul).
  const canModifyCoreData = (() => {
    if (!user || !employee) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    if (user.role === 'SSM_COMPANY_ADMIN' || user.role === 'SSM_INSTRUCTOR') return true;
    if (user.role === 'COMPANY_ADMIN' && user.companyId === employee.companyId) return true;
    if (user.role === 'EMPLOYEE') {
      if (user.canManageEmployees) return true;
      if (user.managesDepartment && employee.departmentId) return true; // CLM — backend verifică dept-ul exact
    }
    return false;
  })();

  // Cine poate vedea parola de acces (afișare/ascundere — backend rămâne autoritatea finală).
  // Doar persoane DIN ACEEAȘI FIRMĂ cu angajatul: admin firmă + HR (bifă/dept) + CLM. Personalul
  // firmei SSM (SSM_COMPANY_ADMIN, SSM_INSTRUCTOR) e exclus. SUPER_ADMIN — acces global.
  const canViewAccessPassword = (() => {
    if (!user || !employee) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    if (user.role === 'SSM_COMPANY_ADMIN' || user.role === 'SSM_INSTRUCTOR') return false;
    if (user.companyId !== employee.companyId) return false;
    if (user.role === 'COMPANY_ADMIN') return true;
    if (user.role === 'EMPLOYEE') {
      if (user.canManageEmployees) return true;
      if (user.managesDepartment && employee.departmentId) return true;
    }
    return false;
  })();

  const handleViewAccessPassword = async () => {
    setAccessPwdLoading(true);
    try {
      const res = await employeesAPI.getAccessPassword(id);
      setAccessPwdState(res.data);
      setAccessPwd(res.data.available ? res.data.password : null);
    } catch (err) {
      showToast(err.response?.data?.error || t('toasts.errorGeneric'), 'error');
    } finally {
      setAccessPwdLoading(false);
    }
  };

  const handleRegenerateAccessPassword = async () => {
    if (!resetPwdReason) return;
    if (resetPwdReason === 'OTHER' && !resetPwdDetails.trim()) return;
    setAccessPwdRegenerating(true);
    try {
      const res = await employeesAPI.regenerateAccessPassword(id, {
        reason: resetPwdReason,
        reasonDetails: resetPwdDetails || undefined,
      });
      setAccessPwdState(res.data);
      setAccessPwd(res.data.password);
      setShowResetPwdModal(false);
      setResetPwdReason('');
      setResetPwdDetails('');
      showToast(t('accessPassword.regenerated'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('toasts.errorGeneric'), 'error');
    } finally {
      setAccessPwdRegenerating(false);
    }
  };

  const copyAccessPassword = async () => {
    if (!accessPwd) return;
    try {
      await navigator.clipboard.writeText(accessPwd);
      showToast(t('accessPassword.copied'), 'success');
    } catch (_) {
      showToast(t('toasts.errorGeneric'), 'error');
    }
  };

  const handleResetSignature = async () => {
    setResettingSignature(true);
    try {
      await api.post(`/employees/${id}/reset-signature`, { reason: resetSignatureReason });
      setShowResetSignatureModal(false);
      setResetSignatureReason('');
      showToast(t('recovery.resetSignatureSuccess'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('toasts.errorGeneric'), 'error');
    } finally {
      setResettingSignature(false);
    }
  };

  const fetchEmployee = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/employees/${id}`);
      setEmployee(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('employeeDetails.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchDepartments = async (companyId) => {
    try {
      const res = await departmentsAPI.getAll(companyId);
      setDepartments(res.data);
    } catch {
      setDepartments([]);
    }
  };

  const openTransferModal = () => {
    if (employee) {
      fetchDepartments(employee.companyId);
      setSelectedDeptId(employee.departmentId || '');
      setShowTransferModal(true);
    }
  };

  const handleTransfer = async () => {
    setTransferring(true);
    try {
      await api.put(`/employees/${id}`, { departmentId: selectedDeptId || null });
      setShowTransferModal(false);
      setSuccess(t('employeeDetails.deptUpdated'));
      fetchEmployee();
    } catch (err) {
      setError(err.response?.data?.error || t('employeeDetails.transferError'));
    } finally {
      setTransferring(false);
    }
  };

  const openJobModal = () => {
    setNewJobTitle(employee?.jobTitle || '');
    setShowJobModal(true);
  };

  const handleJobChange = async () => {
    setSavingJob(true);
    try {
      await api.put(`/employees/${id}`, { jobTitle: newJobTitle });
      setShowJobModal(false);
      setSuccess(t('employeeDetails.jobUpdated'));
      fetchEmployee();
    } catch (err) {
      setError(err.response?.data?.error || t('employeeDetails.jobError'));
    } finally {
      setSavingJob(false);
    }
  };

  // Action mode: 'deactivate' | 'activate' | 'delete'
  const [actionMode, setActionMode] = useState(null);
  const [processing, setProcessing] = useState(false);

  const openActionModal = (mode) => setActionMode(mode);
  const confirmAction = async () => {
    setProcessing(true);
    try {
      if (actionMode === 'delete') {
        await api.delete(`/employees/${id}`);
      } else if (actionMode === 'deactivate') {
        await api.patch(`/employees/${id}/deactivate`);
      } else if (actionMode === 'activate') {
        await api.patch(`/employees/${id}/activate`);
      }
      setActionMode(null);
      if (actionMode === 'delete') {
        // După ștergere angajatul nu mai există → mergem la lista firmei (țintă stabilă), nu navigate(-1).
        navigate(backPath);
      } else {
        fetchEmployee();
      }
    } catch (err) {
      setError(err.response?.data?.error || t('employeeDetails.deleteError'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <Spinner size="lg" label={t('common.loading')} />;

  if (error && !employee) {
    return (
      <div className="space-y-4">
        <Alert type="error">{error}</Alert>
        <Button variant="ghost" onClick={() => navigate('/employees')}>
          <ArrowLeftIcon /> {t('common.back')}
        </Button>
      </div>
    );
  }

  if (!employee) return null;

  const emp = employee;
  const u = emp.user;
  const fullName = `${u.firstName} ${u.lastName}`;

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          employee?.companyId
            ? { label: t('nav.employees'), href: `/employees?companyId=${employee.companyId}` }
            : { label: t('nav.companies'), href: '/companies' },
          { label: fullName || t('employeeDetails.fallbackDetails') },
        ]}
      />
      <PageHeader
        title={fullName}
        subtitle={
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <RoleBadge role={u.role} />
            <StatusBadge status={u.active ? 'active' : 'inactive'} />
            {emp.isCLM && (
              <Badge color="purple" className="text-[10px] font-bold">
                CLM
              </Badge>
            )}
            {emp.hasEPI && (
              <Badge color="red" className="text-[10px] font-bold">
                EPI
              </Badge>
            )}
            {u.isSSMInstructor && (
              <Badge color="blue" className="text-[10px]">
                {t('employeeDetails.instructorSSM')}
              </Badge>
            )}
            {u.isCadruTehnicPSI && (
              <Badge color="orange" className="text-[10px]">
                {t('employeeDetails.cadruPSI')}
              </Badge>
            )}
            {u.canManageEmployees && (
              <Badge color="green" className="text-[10px]">
                {t('employeeDetails.hr')}
              </Badge>
            )}
          </div>
        }
        actions={
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeftIcon /> {t('common.back')}
          </Button>
        }
      />

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onDismiss={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Info + Actiuni */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informatii */}
        <div className="lg:col-span-2">
          <Card>
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">
              {t('companyDetails.info')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label={t('common.email')} value={u.email} />
              <InfoRow label={t('common.phone')} value={u.phone || '—'} />
              <InfoRow label={t('nav.companies')} value={emp.company?.name || '—'} />
              <InfoRow
                label={t('employees.department')}
                value={emp.department ? emp.department.name : <Badge color="yellow">—</Badge>}
              />
              <InfoRow label={t('employees.jobTitle')} value={emp.jobTitle || '—'} />
              <InfoRow label={t('employees.badge')} value={emp.badgeNumber || '—'} />
              <InfoRow
                label={t('employees.hireDate')}
                value={emp.hireDate ? formatDate(emp.hireDate) : '—'}
              />
              <InfoRow label={t('employees.bloodGroup')} value={emp.bloodGroup || '—'} />
              <InfoRow
                label={t('employees.specialAuths')}
                value={emp.specialAuthorizations || '—'}
              />
              {/* #4: indicator CNP — vizibil dacă există (mascat, nu expunem numărul în clar).
                  Setarea/editarea se face din formularul de editare angajat, cu permisiunile aferente. */}
              <InfoRow
                label="CNP"
                value={
                  emp.hasCNP ? (
                    <span className="inline-flex items-center gap-1.5 text-accent-700 dark:text-accent-300">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      ●●●●●●●●●●●●●{' '}
                      <span className="text-xs text-slate-400">{t('employeeDetails.cnpSet')}</span>
                    </span>
                  ) : (
                    <span className="text-warning-600 dark:text-warning-400">
                      {t('employeeDetails.cnpMissing')}
                    </span>
                  )
                }
              />
            </div>
          </Card>
        </div>

        {/* Actiuni */}
        <div>
          <Card>
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">
              {t('common.actions')}
            </h3>
            <div className="space-y-2">
              <Link to={`/employees/${id}/edit`} className="block">
                <Button variant="secondary" size="md" className="w-full justify-start">
                  <EditIcon /> {t('employeeDetails.editEmployee')}
                </Button>
              </Link>
              <Button
                variant="secondary"
                size="md"
                className="w-full justify-start"
                onClick={openTransferModal}
              >
                <SwitchIcon /> {t('employeeDetails.transferDept')}
              </Button>
              <Button
                variant="secondary"
                size="md"
                className="w-full justify-start"
                onClick={openJobModal}
              >
                <EditIcon /> {t('employeeDetails.changeJob')}
              </Button>
              {canModifyCoreData && (
                <>
                  <Button
                    variant="secondary"
                    size="md"
                    className="w-full justify-start"
                    onClick={() => {
                      setNewLastName('');
                      setChangeNameReason('');
                      setChangeNameFile(null);
                      setShowChangeNameModal(true);
                    }}
                    title={t('changeData.changeNameBtnTitle')}
                  >
                    <EditIcon /> {t('changeData.changeNameBtn')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    className="w-full justify-start"
                    onClick={openChangeRouteModal}
                    title={t('changeData.changeRouteBtnTitle')}
                  >
                    <EditIcon /> {t('changeData.changeRouteBtn')}
                  </Button>
                </>
              )}
              <hr className="my-2" />
              {emp.user?.active === false ? (
                <Button
                  variant="ghost"
                  size="md"
                  className="w-full justify-start text-green-700 dark:text-emerald-300 hover:text-green-800 hover:bg-green-50 dark:hover:bg-emerald-950/50"
                  onClick={() => openActionModal('activate')}
                >
                  <TrashIcon /> {t('employees.activateAction')}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="md"
                  className="w-full justify-start text-amber-600 dark:text-amber-300 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/50"
                  onClick={() => openActionModal('deactivate')}
                >
                  <TrashIcon /> {t('employees.deactivateAction')}
                </Button>
              )}
              <Button
                variant="ghost"
                size="md"
                className="w-full justify-start text-danger-600 dark:text-danger-300 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-950/50"
                onClick={() => openActionModal('delete')}
              >
                <TrashIcon /> {t('employees.deleteAction')}
              </Button>

              {user?.role === 'SUPER_ADMIN' && (
                <>
                  <hr className="my-2" />
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 font-bold px-2">
                    {t('recovery.sectionLabel')}
                  </p>
                  <Button
                    variant="ghost"
                    size="md"
                    className="w-full justify-start text-amber-700 dark:text-amber-300 hover:text-amber-800 hover:bg-amber-50 dark:hover:bg-amber-950/50"
                    onClick={() => setShowResetSignatureModal(true)}
                  >
                    <EditIcon /> {t('recovery.resetSignatureBtn')}
                  </Button>
                </>
              )}
            </div>
          </Card>

          {/* Parolă de acces — pentru angajați fără email, responsabilul citește/regenerează parola unică */}
          {canViewAccessPassword && (
            <Card className="mt-6">
              <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                {t('accessPassword.title')}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                {t('accessPassword.description')}
              </p>

              {!accessPwdState && (
                <Button
                  variant="secondary"
                  size="md"
                  className="w-full justify-start"
                  onClick={handleViewAccessPassword}
                  loading={accessPwdLoading}
                >
                  {t('accessPassword.viewBtn')}
                </Button>
              )}

              {accessPwd && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 font-mono text-base tracking-wider select-all break-all">
                      {accessPwd}
                    </code>
                    <Button variant="secondary" size="sm" onClick={copyAccessPassword}>
                      {t('accessPassword.copy')}
                    </Button>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {t('accessPassword.handHint')}
                  </p>
                  {accessPwdState?.passwordEmailSent && (
                    <p className="text-xs text-green-700 dark:text-emerald-300">
                      {t('accessPassword.emailResent')}
                    </p>
                  )}
                </div>
              )}

              {accessPwdState && !accessPwdState.available && (
                <div className="mb-3">
                  <Alert type="info">
                    {accessPwdState.reason === 'PASSWORD_ALREADY_CHANGED'
                      ? t('accessPassword.alreadyChanged')
                      : t('accessPassword.noStored')}
                  </Alert>
                </div>
              )}

              {accessPwdState && (
                <Button
                  variant="ghost"
                  size="md"
                  className="w-full justify-start mt-2"
                  onClick={() => {
                    setResetPwdReason('');
                    setResetPwdDetails('');
                    setShowResetPwdModal(true);
                  }}
                >
                  {t('accessPassword.regenerateBtn')}
                </Button>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Instruiri — tabel unificat (instruiri + cursuri neinceputé) */}
      <CollapsibleTrainingsCard
        trainings={emp.trainings}
        assignments={emp.assignments}
        user={user}
        onReload={fetchEmployee}
      />

      {canModifyCoreData && <ChangeHistorySection employeeId={id} />}

      {/* Monitorizare IP — vizibil dacă poate edita cel puțin un câmp */}
      {canEditAnyIPForEmployeeCompany(user, emp.company) && (
        <IPMonitoringSection
          employee={emp}
          onUpdate={fetchEmployee}
          canEditSSM={canEditIPFieldForEmployeeCompany(user, emp.company, 'ssm')}
          canEditSU={canEditIPFieldForEmployeeCompany(user, emp.company, 'su')}
        />
      )}

      {/* Modal: Transfer departament */}
      <Modal
        open={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title={t('employees.department')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowTransferModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleTransfer} loading={transferring}>
              {transferring ? t('employeeDetails.transferring') : t('employeeDetails.transfer')}
            </Button>
          </div>
        }
      >
        <Select
          label={t('employees.department')}
          value={selectedDeptId}
          onChange={(e) => setSelectedDeptId(e.target.value)}
          placeholder={t('employeeDetails.noDeptPlaceholder')}
          options={departments.map((d) => ({ value: d.id, label: d.name }))}
        />
      </Modal>

      {/* Modal: Schimba functia */}
      <Modal
        open={showJobModal}
        onClose={() => setShowJobModal(false)}
        title={t('employees.jobTitle')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowJobModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleJobChange} loading={savingJob}>
              {savingJob ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        }
      >
        <Input
          label={t('employees.jobTitle')}
          value={newJobTitle}
          onChange={(e) => setNewJobTitle(e.target.value)}
          placeholder={t('employeeDetails.jobTitleExample')}
        />
      </Modal>

      <ConfirmModal
        open={!!actionMode}
        onClose={() => setActionMode(null)}
        onConfirm={confirmAction}
        title={
          actionMode === 'delete'
            ? t('employees.deleteTitle')
            : actionMode === 'activate'
              ? t('employees.activateTitle')
              : t('employees.deactivateTitle')
        }
        message={
          actionMode === 'delete'
            ? t('employees.deleteMsg')
            : actionMode === 'activate'
              ? t('employees.activateMsg')
              : t('employees.deactivateMsg')
        }
        confirmLabel={
          actionMode === 'delete'
            ? t('employees.deleteConfirm')
            : actionMode === 'activate'
              ? t('employees.activate')
              : t('employees.deactivate')
        }
        cancelLabel={t('common.cancel')}
        variant={actionMode === 'activate' ? 'primary' : 'danger'}
        loading={processing}
      />

      <Modal
        open={showChangeNameModal}
        onClose={() => !changingName && setShowChangeNameModal(false)}
        closable={!changingName}
        title={t('changeData.changeNameTitle')}
        size="lg"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button
              variant="secondary"
              onClick={() => setShowChangeNameModal(false)}
              disabled={changingName}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleChangeName}
              loading={changingName}
              disabled={changingName || !newLastName.trim()}
            >
              {t('changeData.confirmBtn')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
          <p>{t('changeData.changeNameIntro')}</p>

          <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-md">
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              {t('changeData.currentName')}
            </p>
            <p className="font-medium text-slate-900 dark:text-slate-100">
              {employee?.user?.firstName} {employee?.user?.lastName}
            </p>
          </div>

          <Input
            label={t('changeData.newLastName')}
            value={newLastName}
            onChange={(e) => setNewLastName(e.target.value)}
            placeholder={t('changeData.newLastNamePh')}
            required
          />

          <Input
            label={t('changeData.reason')}
            value={changeNameReason}
            onChange={(e) => setChangeNameReason(e.target.value)}
            placeholder={t('changeData.reasonPh')}
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('changeData.supportingDoc')}
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setChangeNameFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-700 dark:text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/50 dark:file:text-primary-200"
            />
            {changeNameFile && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('changeData.fileSelected', {
                  name: changeNameFile.name,
                  size: Math.round(changeNameFile.size / 1024),
                })}
              </p>
            )}
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              {t('changeData.supportingDocHint')}
            </p>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-md">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
              {t('employeeDetails.whatHappens')}
            </p>
            <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5 list-disc pl-5">
              <li>{t('employeeDetails.changeSteps.declaration')}</li>
              <li>{t('employeeDetails.changeSteps.notify')}</li>
              <li>{t('employeeDetails.changeSteps.afterSign')}</li>
              <li>{t('employeeDetails.changeSteps.nameStrikethrough')}</li>
              <li>{t('employeeDetails.changeSteps.annex')}</li>
              <li>{t('employeeDetails.changeSteps.nameDoc')}</li>
              <li>{t('employeeDetails.changeSteps.blocked')}</li>
            </ul>
          </div>
        </div>
      </Modal>

      <Modal
        open={showChangeRouteModal}
        onClose={() => !changingRoute && setShowChangeRouteModal(false)}
        closable={!changingRoute}
        title={t('changeData.changeRouteTitle')}
        size="lg"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button
              variant="secondary"
              onClick={() => setShowChangeRouteModal(false)}
              disabled={changingRoute}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleChangeRoute}
              loading={changingRoute}
              disabled={changingRoute}
            >
              {t('changeData.confirmBtn')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
          <p>{t('changeData.changeRouteIntro')}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label={t('changeData.homeAddress')}
              value={routeForm.homeAddress}
              onChange={(e) => setRouteForm({ ...routeForm, homeAddress: e.target.value })}
              placeholder={t('changeData.homeAddressPh')}
            />
            <Input
              label={t('changeData.residenceAddress')}
              value={routeForm.residenceAddress}
              onChange={(e) => setRouteForm({ ...routeForm, residenceAddress: e.target.value })}
              placeholder={t('changeData.residenceAddressPh')}
            />
            <Input
              label={t('changeData.workAddress')}
              value={routeForm.workAddress}
              onChange={(e) => setRouteForm({ ...routeForm, workAddress: e.target.value })}
              placeholder={t('changeData.workAddressPh')}
            />
            <Input
              label={t('changeData.routeDuration')}
              type="number"
              value={routeForm.routeDuration}
              onChange={(e) => setRouteForm({ ...routeForm, routeDuration: e.target.value })}
              placeholder={t('changeData.routeDurationPh')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('changeData.transportMode')}{' '}
              <span className="font-normal text-slate-400">
                · {t('myRoute.transportModeHelper')}
              </span>
            </label>
            {(() => {
              const selected = routeForm.transportMode
                ? routeForm.transportMode
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [];
              const opts = [
                { v: 'personal', l: t('changeData.transportPersonal') },
                { v: 'public', l: t('changeData.transportPublic') },
                { v: 'pe_jos', l: t('changeData.transportWalk') },
                { v: 'bicicleta', l: t('changeData.transportBike') },
                { v: 'altele', l: t('myRoute.transport.other') },
              ];
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {opts.map((o) => (
                    <div key={o.v}>
                      <label
                        className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-sm ${selected.includes(o.v) ? 'border-accent-500 bg-accent-50 dark:bg-accent-950/40' : 'border-slate-300 dark:border-slate-700'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(o.v)}
                          onChange={() => toggleRouteTransport(o.v)}
                          className="h-4 w-4 rounded text-accent-600"
                        />
                        {o.l}
                      </label>
                      {o.v === 'altele' && selected.includes('altele') && (
                        <input
                          type="text"
                          value={routeForm.transportModeOther}
                          onChange={(e) =>
                            setRouteForm({ ...routeForm, transportModeOther: e.target.value })
                          }
                          placeholder={t('myRoute.transportOtherPlaceholder')}
                          className="w-full mt-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('changeData.routeDetails')}
            </label>
            <textarea
              rows={3}
              value={routeForm.routeDetails}
              onChange={(e) => setRouteForm({ ...routeForm, routeDetails: e.target.value })}
              placeholder={t('changeData.routeDetailsPh')}
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
            />
          </div>

          <Input
            label={t('changeData.reason')}
            value={changeRouteReason}
            onChange={(e) => setChangeRouteReason(e.target.value)}
            placeholder={t('changeData.changeRouteReasonPh')}
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('changeData.supportingDoc')}
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setChangeRouteFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-700 dark:text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/50 dark:file:text-primary-200"
            />
            {changeRouteFile && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('changeData.fileSelected', {
                  name: changeRouteFile.name,
                  size: Math.round(changeRouteFile.size / 1024),
                })}
              </p>
            )}
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-md">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
              {t('employeeDetails.whatHappens')}
            </p>
            <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5 list-disc pl-5">
              <li>{t('employeeDetails.changeSteps.declaration')}</li>
              <li>{t('employeeDetails.changeSteps.notify')}</li>
              <li>{t('employeeDetails.changeSteps.afterSign')}</li>
              <li>{t('employeeDetails.changeSteps.routeStrikethrough')}</li>
              <li>{t('employeeDetails.changeSteps.annex')}</li>
              <li>{t('employeeDetails.changeSteps.routeDoc')}</li>
              <li>{t('employeeDetails.changeSteps.blocked')}</li>
            </ul>
          </div>
        </div>
      </Modal>

      <Modal
        open={showResetSignatureModal}
        onClose={() => !resettingSignature && setShowResetSignatureModal(false)}
        closable={!resettingSignature}
        title={t('recovery.resetSignatureTitle')}
        size="md"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button
              variant="secondary"
              onClick={() => setShowResetSignatureModal(false)}
              disabled={resettingSignature}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleResetSignature}
              loading={resettingSignature}
              disabled={resettingSignature}
            >
              {t('recovery.resetSignatureConfirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <p>{t('recovery.resetSignatureDesc')}</p>
          <p className="text-amber-700 dark:text-amber-300 font-medium">
            {t('recovery.resetSignatureWarn')}
          </p>
          <Input
            label={t('recovery.resetSignatureReason')}
            value={resetSignatureReason}
            onChange={(e) => setResetSignatureReason(e.target.value)}
            placeholder={t('recovery.resetSignatureReasonPh')}
          />
        </div>
      </Modal>

      <Modal
        open={showResetPwdModal}
        onClose={() => !accessPwdRegenerating && setShowResetPwdModal(false)}
        closable={!accessPwdRegenerating}
        title={t('accessPassword.resetModalTitle')}
        size="md"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button
              variant="secondary"
              onClick={() => setShowResetPwdModal(false)}
              disabled={accessPwdRegenerating}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleRegenerateAccessPassword}
              loading={accessPwdRegenerating}
              disabled={
                accessPwdRegenerating ||
                !resetPwdReason ||
                (resetPwdReason === 'OTHER' && !resetPwdDetails.trim())
              }
            >
              {t('accessPassword.resetConfirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <p>{t('accessPassword.resetModalDesc')}</p>
          <Select
            label={t('accessPassword.reasonLabel')}
            value={resetPwdReason}
            onChange={(e) => setResetPwdReason(e.target.value)}
            placeholder={t('accessPassword.reasonPlaceholder')}
            options={[
              { value: 'NO_EMAIL', label: t('accessPassword.reasonNoEmail') },
              { value: 'FORGOT', label: t('accessPassword.reasonForgot') },
              { value: 'REQUESTED', label: t('accessPassword.reasonRequested') },
              { value: 'OTHER', label: t('accessPassword.reasonOther') },
            ]}
          />
          {resetPwdReason === 'OTHER' && (
            <Input
              label={t('accessPassword.reasonDetailsLabel')}
              value={resetPwdDetails}
              onChange={(e) => setResetPwdDetails(e.target.value)}
              placeholder={t('accessPassword.reasonDetailsPh')}
            />
          )}
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('accessPassword.resetNotifyHint')}
          </p>
        </div>
      </Modal>
    </div>
  );
};

const COLLAPSED_ROWS = 3;

const CollapsibleTrainingsCard = ({ trainings, assignments, user, onReload }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // Recovery modals per rând
  const [pendingAction, setPendingAction] = useState(null); // { trainingId, mode: 'invalidate' | 'reset', title }
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const runAction = async () => {
    if (!pendingAction) return;
    setProcessing(true);
    try {
      await api.post(`/trainings/${pendingAction.trainingId}/invalidate-employee-signature`, {
        reason,
      });
      showToast(t('recovery.invalidateSuccess'), 'success');
      setPendingAction(null);
      setReason('');
      if (onReload) onReload();
    } catch (err) {
      showToast(err.response?.data?.error || t('toasts.errorGeneric'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Combină: instruiri existente + cursuri asignate neinceputé (ca rânduri cu status "Neinceput")
  const trainingCourseIds = new Set((trainings || []).map((t) => t.course?.id).filter(Boolean));
  const pendingAssignments = (assignments || []).filter(
    (a) => !trainingCourseIds.has(a.course?.id)
  );

  const allRows = [
    ...(trainings || []).map((t) => ({
      key: `t-${t.id}`,
      trainingId: t.id,
      title: t.course?.title || '—',
      type: t.course?.type || '—',
      status: t.status,
      progress: t.progress,
      score: t.score,
      date: t.startedAt,
      isPending: false,
    })),
    ...pendingAssignments.map((a) => ({
      key: `a-${a.id}`,
      trainingId: null,
      title: a.course?.title || '—',
      type: a.course?.type || '—',
      status: 'NOT_STARTED',
      progress: null,
      score: null,
      date: a.assignedAt,
      isPending: true,
    })),
  ];

  const total = allRows.length;
  const visibleRows = expanded ? allRows : allRows.slice(0, COLLAPSED_ROWS);
  const hasMore = total > COLLAPSED_ROWS;

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
          {t('employeeDetails.trainingsTitle', { count: total })}
        </h3>
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-primary-600 dark:text-primary-300 hover:text-primary-800 font-medium flex items-center gap-1"
          >
            {expanded
              ? t('employeeDetails.collapse')
              : t('employeeDetails.seeAll', { count: total })}
            <svg
              className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
      {total > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="table-header">{t('employeeDetails.colCourse')}</th>
                <th className="table-header">{t('employeeDetails.colType')}</th>
                <th className="table-header">{t('employeeDetails.colStatus')}</th>
                <th className="table-header">{t('employeeDetails.colProgress')}</th>
                <th className="table-header">{t('employeeDetails.colScore')}</th>
                <th className="table-header">{t('employeeDetails.colDate')}</th>
                {isSuperAdmin && (
                  <th className="table-header text-right">{t('recovery.actionsCol')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const clickable = !!row.trainingId;
                return (
                  <tr
                    key={row.key}
                    onClick={
                      clickable ? () => navigate(`/training-details/${row.trainingId}`) : undefined
                    }
                    className={`table-row ${row.isPending ? 'opacity-60' : ''} ${clickable ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800' : ''}`}
                    title={clickable ? t('employeeDetails.recoveryRowTooltip') : undefined}
                  >
                    <td className="table-cell text-sm font-medium text-slate-900 dark:text-slate-100">
                      {row.title}
                    </td>
                    <td className="table-cell">
                      <Badge color="blue">{row.type}</Badge>
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="table-cell text-sm text-slate-600 dark:text-slate-300">
                      {row.progress != null ? `${Math.round(row.progress)}%` : '—'}
                    </td>
                    <td className="table-cell text-sm text-slate-600 dark:text-slate-300">
                      {row.score != null ? `${row.score}%` : '—'}
                    </td>
                    <td className="table-cell text-sm text-slate-500 dark:text-slate-400">
                      {row.date ? formatDate(row.date) : '—'}
                    </td>
                    {isSuperAdmin && (
                      <td className="table-cell text-right" onClick={(e) => e.stopPropagation()}>
                        {row.trainingId ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-amber-700 hover:text-amber-800"
                            title={t('recovery.invalidateBtnTitle')}
                            onClick={() => {
                              setPendingAction({
                                trainingId: row.trainingId,
                                mode: 'invalidate',
                                title: row.title,
                              });
                              setReason('');
                            }}
                          >
                            {t('recovery.invalidateBtn')}
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!expanded && hasMore && (
            <button
              onClick={() => setExpanded(true)}
              className="w-full py-2 text-xs text-primary-600 dark:text-primary-300 hover:text-primary-800 hover:bg-primary-50 dark:hover:bg-primary-950/50 font-medium text-center border-t border-slate-100 dark:border-slate-800 transition"
            >
              {t('employeeDetails.hiddenRows', { count: total - COLLAPSED_ROWS })}
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('employeeDetails.noTrainings')}
        </p>
      )}

      {isSuperAdmin && (
        <Modal
          open={!!pendingAction}
          onClose={() => !processing && setPendingAction(null)}
          closable={!processing}
          title={t('recovery.invalidateTitle')}
          size="md"
          footer={
            <div className="flex gap-3 justify-end w-full">
              <Button
                variant="secondary"
                onClick={() => setPendingAction(null)}
                disabled={processing}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={runAction}
                loading={processing}
                disabled={processing}
              >
                {t('recovery.invalidateConfirm')}
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <p className="font-medium text-slate-900 dark:text-slate-100">
              {t('recovery.invalidateCourse', { title: pendingAction?.title })}
            </p>
            <p>{t('recovery.invalidateDesc')}</p>
            <p className="text-amber-700 dark:text-amber-300 font-medium">
              {t('recovery.invalidateWarn')}
            </p>
            <Input
              label={t('recovery.invalidateReason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('recovery.invalidateReasonPh')}
            />
          </div>
        </Modal>
      )}
    </Card>
  );
};

const IPMonitoringSection = ({ employee, onUpdate, canEditSSM = true, canEditSU = true }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [ssmDays, setSsmDays] = useState(employee.ipSSMIntervalDays ?? null);
  const [suDays, setSuDays] = useState(employee.ipSUIntervalDays ?? null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Firma poate fi configurată doar pentru SSM, doar pentru SU sau pentru ambele.
  // Dacă un serviciu nu e oferit, blocul corespunzător NU se afișează — n-are sens să configurezi
  // periodicitatea pentru un IP pe care firma n-o gestionează.
  // `offersSSM/offersSU` lipsă din răspuns (companii vechi) → tratăm ca true (default permisiv).
  const showSSM = employee.company?.offersSSM !== false;
  const showSU = employee.company?.offersSU !== false;

  // Calculează deadline-urile
  const getDeadlineInfo = (type) => {
    const intervalDays = type === 'SSM' ? employee.ipSSMIntervalDays : employee.ipSUIntervalDays;
    if (!intervalDays) return null;

    const ipType = type === 'SSM' ? 'IP_SSM' : 'IP_SU';
    const iigTypes = type === 'SSM' ? ['IIG_SSM', 'ILM_SSM'] : ['IIG_SU', 'ILM_SU'];

    // Caută ultimul training completat relevant
    const lastTraining =
      employee.trainings?.find((t) => t.passed && t.course?.type === ipType) ||
      employee.trainings?.find((t) => t.passed && iigTypes.includes(t.course?.type));

    if (!lastTraining?.completedAt)
      return { status: 'no-training', message: t('ipMonitoring.noTraining') };

    const dueDate = new Date(lastTraining.completedAt);
    dueDate.setDate(dueDate.getDate() + intervalDays);
    const now = new Date();
    const daysLeft = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    const dateStr = formatDate(dueDate);
    const lastDateStr = formatDate(lastTraining.completedAt);

    return { daysLeft, dateStr, lastDateStr, dueDate };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (canEditSSM) payload.ipSSMIntervalDays = ssmDays || null;
      if (canEditSU) payload.ipSUIntervalDays = suDays || null;
      await api.put(`/employees/${employee.id}`, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdate();
    } catch (err) {
      showToast(err.response?.data?.error || t('toasts.errorSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    (canEditSSM && (ssmDays || null) !== (employee.ipSSMIntervalDays ?? null)) ||
    (canEditSU && (suDays || null) !== (employee.ipSUIntervalDays ?? null));

  const ssmDeadline = getDeadlineInfo('SSM');
  const suDeadline = getDeadlineInfo('SU');

  const DeadlineBadge = ({ info }) => {
    if (!info)
      return (
        <span className="text-xs text-slate-400 dark:text-slate-400">
          {t('ipMonitoring.noInterval')}
        </span>
      );
    if (info.status === 'no-training')
      return <span className="text-xs text-slate-400 dark:text-slate-400">{info.message}</span>;

    let color, bg;
    if (info.daysLeft < 0) {
      color = 'text-danger-700 dark:text-danger-300';
      bg = 'bg-danger-50 dark:bg-danger-950/50 border-danger-200 dark:border-danger-700';
    } else if (info.daysLeft <= 7) {
      color = 'text-danger-700 dark:text-danger-300';
      bg = 'bg-danger-50 dark:bg-danger-950/50 border-danger-200 dark:border-danger-700';
    } else if (info.daysLeft <= 30) {
      color = 'text-warning-700 dark:text-warning-300';
      bg = 'bg-warning-50 dark:bg-warning-950/50 border-warning-200 dark:border-warning-700';
    } else {
      color = 'text-accent-700 dark:text-accent-300';
      bg = 'bg-accent-50 dark:bg-accent-950/50 border-accent-200 dark:border-accent-700';
    }

    return (
      <div className={`${bg} border rounded-lg px-3 py-2`}>
        <p className={`text-sm font-semibold ${color}`}>
          {info.daysLeft < 0
            ? t('ipMonitoring.expiredDays', { count: Math.abs(info.daysLeft) })
            : t('ipMonitoring.daysRemaining', { count: info.daysLeft })}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('ipMonitoring.dueOn', { date: info.dateStr })}
        </p>
        <p className="text-xs text-slate-400 dark:text-slate-400">
          {t('ipMonitoring.lastTraining', { date: info.lastDateStr })}
        </p>
      </div>
    );
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
          {t('ipMonitoring.title')}
        </h3>
        {saved && (
          <span className="text-xs text-accent-600 dark:text-accent-300 font-medium">
            {t('ipMonitoring.saved')}
          </span>
        )}
      </div>

      {!showSSM && !showSU && (
        <p className="text-xs text-slate-500 dark:text-slate-400 italic">
          {t('ipMonitoring.companyNoServices', {
            defaultValue:
              'Firma acestui angajat nu are configurat niciun serviciu (SSM sau SU). Activați un serviciu din setările firmei pentru a putea seta periodicități.',
          })}
        </p>
      )}

      <div className={`grid grid-cols-1 ${showSSM && showSU ? 'md:grid-cols-2' : ''} gap-8`}>
        {/* IP SSM — afișat doar dacă firma oferă serviciul SSM */}
        {showSSM && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge color="blue">IP SSM</Badge>
              {!canEditSSM && (
                <span className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                  {t('ipMonitoring.readOnlySSM')}
                </span>
              )}
            </div>
            {canEditSSM ? (
              <IntervalPicker value={ssmDays} onChange={setSsmDays} />
            ) : (
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {ssmDays ? (
                  <strong>{t('ipMonitoring.setValue', { value: ssmDays })}</strong>
                ) : (
                  <span className="text-slate-400 dark:text-slate-400">
                    {t('ipMonitoring.notSet')}
                  </span>
                )}
              </p>
            )}
            <DeadlineBadge info={ssmDeadline} />
            {ssmDeadline && ssmDeadline.daysLeft !== undefined && ssmDeadline.daysLeft <= 7 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate(
                    `/assign-courses?employeeId=${employee.id}&companyId=${employee.companyId}`
                  )
                }
              >
                {t('ipMonitoring.sendIpSSM')}
              </Button>
            )}
          </div>
        )}

        {/* IP SU — afișat doar dacă firma oferă serviciul SU */}
        {showSU && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge color="orange">IP SU</Badge>
              {employee.hasEPI && (
                <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-slate-800 border border-amber-200 dark:border-slate-700 rounded-full px-2 py-0.5">
                  {t('ipMonitoring.epiRecommended')}
                </span>
              )}
              {!canEditSU && (
                <span className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                  {t('ipMonitoring.readOnlySU')}
                </span>
              )}
            </div>
            {canEditSU ? (
              <IntervalPicker value={suDays} onChange={setSuDays} />
            ) : (
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {suDays ? (
                  <strong>{t('ipMonitoring.setValue', { value: suDays })}</strong>
                ) : (
                  <span className="text-slate-400 dark:text-slate-400">
                    {t('ipMonitoring.notSet')}
                  </span>
                )}
              </p>
            )}
            <DeadlineBadge info={suDeadline} />
            {suDeadline && suDeadline.daysLeft !== undefined && suDeadline.daysLeft <= 7 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate(
                    `/assign-courses?employeeId=${employee.id}&companyId=${employee.companyId}`
                  )
                }
              >
                {t('ipMonitoring.sendIpSU')}
              </Button>
            )}
          </div>
        )}
      </div>

      {hasChanges && (
        <div className="mt-4 flex justify-end">
          <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>
            {saving ? t('common.saving') : t('ipMonitoring.saveIntervals')}
          </Button>
        </div>
      )}

      {!employee.ipSSMIntervalDays && !employee.ipSUIntervalDays && (
        <p className="text-xs text-slate-400 dark:text-slate-400 mt-3">
          {t('ipMonitoring.setupHint')}
        </p>
      )}
    </Card>
  );
};

// Secțiune care afișează toate modificările istorice (nume, traseu, adrese) ale angajatului
const ChangeHistorySection = ({ employeeId }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/employees/${employeeId}/change-logs`)
      .then((res) => {
        if (!cancelled) setLogs(res.data);
      })
      .catch((err) => {
        if (!cancelled)
          showToast(err.response?.data?.error || t('changeHistory.loadError'), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const formatValue = (changeType, value) => {
    if (!value) return '—';
    if (changeType === 'NAME') {
      return `${value.firstName || ''} ${value.lastName || ''}`.trim() || '—';
    }
    if (changeType === 'ROUTE') {
      const parts = [];
      if (value.homeAddress)
        parts.push(t('employeeDetails.histHomeAddress', { value: value.homeAddress }));
      if (value.residenceAddress)
        parts.push(t('employeeDetails.histResidence', { value: value.residenceAddress }));
      if (value.workAddress)
        parts.push(t('employeeDetails.histWork', { value: value.workAddress }));
      if (value.transportMode) {
        const tm = String(value.transportMode)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((c) =>
            c === 'altele'
              ? value.transportModeOther
                ? t('employeeDetails.transportOtherWith', { value: value.transportModeOther })
                : t('employeeDetails.transportOther')
              : c
          )
          .join(', ');
        parts.push(t('employeeDetails.histTransport', { value: tm }));
      }
      if (value.routeDuration)
        parts.push(t('employeeDetails.histDuration', { value: value.routeDuration }));
      if (value.routeDetails)
        parts.push(t('employeeDetails.histDescription', { value: value.routeDetails }));
      return parts.length ? parts.join(' · ') : '—';
    }
    return JSON.stringify(value);
  };

  const labelForType = (type) =>
    ({
      NAME: t('changeHistory.typeName'),
      ROUTE: t('changeHistory.typeRoute'),
      HOME_ADDRESS: t('changeHistory.typeHomeAddress'),
      WORK_ADDRESS: t('changeHistory.typeWorkAddress'),
    })[type] || type;

  const statusBadge = (status) => {
    if (status === 'APPROVED')
      return { color: 'green', label: t('employeeDetails.histStatusApproved') };
    if (status === 'PENDING_EMPLOYEE_SIGNATURE')
      return { color: 'yellow', label: t('employeeDetails.histStatusPendingEmployee') };
    if (status === 'PENDING_RESPONSIBLE_SIGNATURE')
      return { color: 'yellow', label: t('employeeDetails.histStatusPendingResponsible') };
    if (status === 'REJECTED')
      return { color: 'red', label: t('employeeDetails.histStatusRejected') };
    return { color: 'gray', label: status };
  };

  const downloadDoc = (logId, fileName) => {
    // Deschide într-un tab nou ca să primească răspunsul de download
    const token = localStorage.getItem('authToken');
    const url = `${api.defaults.baseURL}/employees/${employeeId}/change-logs/${logId}/document`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error(t('changeHistory.downloadError'));
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = fileName || 'document';
        a.click();
      })
      .catch((err) => showToast(err.message, 'error'));
  };

  return (
    <Card>
      <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4">
        {t('changeHistory.title')}
      </h3>
      {loading ? (
        <Spinner label={t('changeHistory.loading')} />
      ) : !logs || logs.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('changeHistory.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {logs.map((log) => (
            <li
              key={log.id}
              className="border border-slate-200 dark:border-slate-800 rounded-lg p-3"
            >
              <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge color={log.changeType === 'NAME' ? 'blue' : 'purple'}>
                    {labelForType(log.changeType)}
                  </Badge>
                  {(() => {
                    const s = statusBadge(log.status);
                    return <Badge color={s.color}>{s.label}</Badge>;
                  })()}
                  {log.initiatedBy === 'EMPLOYEE' && (
                    <Badge color="gray" className="text-[10px]">
                      {t('employeeDetails.histEmployeeRequest')}
                    </Badge>
                  )}
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {formatDate(log.changedAt)} ·{' '}
                    {new Date(log.changedAt).toLocaleTimeString('ro-RO')}
                  </span>
                </div>
                {log.supportingDocPath && (
                  <button
                    onClick={() => downloadDoc(log.id, log.supportingDocName)}
                    className="text-xs text-primary-600 dark:text-primary-300 hover:underline flex items-center gap-1"
                  >
                    📎 {log.supportingDocName || t('employeeDetails.histSupportingDoc')}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('changeHistory.old')}
                  </p>
                  <p className="text-slate-700 dark:text-slate-300">
                    {formatValue(log.changeType, log.oldValue)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('changeHistory.new')}
                  </p>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {formatValue(log.changeType, log.newValue)}
                  </p>
                </div>
              </div>
              {log.reason && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  <span className="font-medium">{t('changeHistory.reason')}</span> {log.reason}
                </p>
              )}
              {log.responsible && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  <span className="font-medium">{t('changeHistory.confirmedBy')}</span>{' '}
                  {log.responsible.firstName} {log.responsible.lastName} ({log.responsible.role})
                  {log.hasResponsibleSignature && ` · ${t('changeHistory.signed')}`}
                  {log.hasEmployeeSignature && ` · ${t('changeHistory.signedByEmployee')}`}
                </p>
              )}
              {log.status === 'REJECTED' && (
                <p className="text-xs text-danger-600 dark:text-danger-400 mt-1">
                  <span className="font-medium">
                    {log.rejectedBy
                      ? t('employeeDetails.histRejectedBy', {
                          name: `${log.rejectedBy.firstName} ${log.rejectedBy.lastName}`,
                        })
                      : t('employeeDetails.histStatusRejected')}
                    :
                  </span>{' '}
                  {log.rejectionReason || '—'}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const InfoRow = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
      {label}
    </dt>
    <dd className="mt-1 text-sm text-slate-900 dark:text-slate-100">
      {typeof value === 'string' ? value : value}
    </dd>
  </div>
);

export default EmployeeDetails;
